import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatNumber, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Fuel, Upload, Loader2, TrendingDown, TrendingUp, Truck, Cog, AlertTriangle,
  Search, FileSpreadsheet, ArrowUpDown, CheckCircle2, XCircle, Info,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const STATUS_STYLES = {
  high: { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", dot: "bg-red-500", label: "Yüksek" },
  low: { bg: "bg-green-50", border: "border-green-300", text: "text-green-800", dot: "bg-green-500", label: "Verimli" },
  normal: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-700", dot: "bg-slate-400", label: "Normal" },
  unknown: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", dot: "bg-amber-400", label: "Veri Yok" },
};

function KPICard({ label, value, subtext, icon: Icon, tone = "slate", testId }) {
  const tones = {
    slate: "text-slate-900",
    sky: "text-sky-700",
    green: "text-green-700",
    red: "text-red-700",
    amber: "text-amber-700",
  };
  return (
    <div className="site-card p-4 sm:p-5 rounded-lg" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon size={18} className="text-slate-400" />}
      </div>
      <div className={`font-display font-black text-2xl sm:text-3xl ${tones[tone]} tabular-nums`}>{value}</div>
      {subtext && <div className="text-xs text-slate-500 mt-1">{subtext}</div>}
    </div>
  );
}

export default function FuelAnalysisPage() {
  const { user } = useAuth();
  const isEditor = user?.role === "admin" || user?.role === "editor";
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);   // last uploaded file (kept in memory for re-analysis)
  const [selectedSheets, setSelectedSheets] = useState(null);  // null = all
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("unit_per_litre");
  const [sortAsc, setSortAsc] = useState(true);
  const [loadingLast, setLoadingLast] = useState(true);

  useEffect(() => {
    api.get("/fuel/last").then((r) => {
      setAnalysis(r.data);
      if (r.data?.processed_sheets) setSelectedSheets(new Set(r.data.processed_sheets));
    }).catch(() => {}).finally(() => setLoadingLast(false));
  }, []);

  const handleFile = async (f, sheetsToUse = null) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (sheetsToUse && sheetsToUse.size > 0) {
        fd.append("sheets", JSON.stringify([...sheetsToUse]));
      }
      const res = await api.post("/fuel/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(res.data);
      setCurrentFile(f);
      // Initialize sheet selection to what was actually processed
      setSelectedSheets(new Set(res.data.processed_sheets || res.data.all_sheets || []));
      const sheetInfo = res.data.processed_sheets?.length
        ? ` (${res.data.processed_sheets.length}/${res.data.all_sheets?.length || "?"} sheet)`
        : "";
      toast.success(`${res.data.row_count} kayıt işlendi · ${res.data.vehicles.length} araç${sheetInfo}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const reAnalyze = () => {
    if (!currentFile) {
      toast.error("Yeniden analiz için dosyayı tekrar yükleyin");
      return;
    }
    if (!selectedSheets || selectedSheets.size === 0) {
      toast.error("En az bir sheet seçin");
      return;
    }
    handleFile(currentFile, selectedSheets);
  };

  const toggleSheet = (sn) => {
    setSelectedSheets((prev) => {
      const next = new Set(prev || []);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };
  const selectAllSheets = () => {
    if (analysis?.all_sheets) setSelectedSheets(new Set(analysis.all_sheets));
  };
  const clearSheets = () => setSelectedSheets(new Set());

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const filtered = useMemo(() => {
    if (!analysis?.vehicles) return [];
    let list = analysis.vehicles;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((v) => v.plate.toLowerCase().includes(q) || (v.drivers || []).some((d) => d.toLowerCase().includes(q)));
    }
    if (typeFilter !== "all") list = list.filter((v) => v.vehicle_type === typeFilter);
    if (statusFilter !== "all") list = list.filter((v) => v.status === statusFilter);
    list = [...list].sort((a, b) => {
      const av = a[sortField] ?? -1;
      const bv = b[sortField] ?? -1;
      if (typeof av === "string") return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? av - bv : bv - av;
    });
    return list;
  }, [analysis, search, typeFilter, statusFilter, sortField, sortAsc]);

  const chartData = useMemo(() => {
    if (!analysis?.vehicles) return [];
    return analysis.vehicles
      .filter((v) => v.unit_per_litre && v.vehicle_type === "Karayolu")
      .slice(0, 15)   // vehicles are pre-sorted worst→best, so top 15 are the least efficient
      .map((v) => ({ plate: v.plate, km_per_litre: v.unit_per_litre, status: v.status }));
  }, [analysis]);

  const exportExcel = async () => {
    try {
      const res = await api.get("/fuel/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `yakit_analizi_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc((v) => !v);
    else { setSortField(field); setSortAsc(false); }
  };

  const s = analysis?.summary;

  return (
    <div className="space-y-6" data-testid="fuel-analysis-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-sky-600 mb-1">Filo Yönetimi</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tight flex items-center gap-3">
            <Fuel size={30} className="text-sky-500" /> Yakıt Analizi
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">
            Excel dosyanızı yükleyin — sistem plakalara göre otomatik gruplandırır, km başına yakıt tüketimini hesaplar ve yüksek tüketim yapan araçları uyarır.
          </p>
        </div>
        {analysis?.vehicles?.length > 0 && (
          <Button variant="outline" onClick={exportExcel} className="border-2 border-slate-300 font-semibold h-11" data-testid="fuel-export-excel">
            <FileSpreadsheet size={16} className="mr-2" /> Analizi Excel'e Aktar
          </Button>
        )}
      </div>

      {/* Upload zone */}
      {isEditor && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`
            site-card cursor-pointer p-6 sm:p-10 rounded-lg border-2 border-dashed transition-all
            ${dragOver ? "border-sky-500 bg-sky-50" : "border-slate-300 hover:border-sky-400 hover:bg-sky-50/40"}
            ${uploading ? "opacity-70 cursor-wait" : ""}
          `}
          data-testid="fuel-upload-zone"
        >
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".xlsx,.xlsm,.xls,.csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="fuel-upload-input"
          />
          <div className="flex flex-col items-center gap-3 text-center">
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-colors ${dragOver ? "bg-sky-500 text-white" : "bg-sky-100 text-sky-600"}`}>
              {uploading ? <Loader2 className="animate-spin" size={28} /> : <Upload size={28} />}
            </div>
            <div>
              <div className="font-black text-base sm:text-lg text-slate-900">
                {uploading ? "Dosya işleniyor..." : "Excel / CSV Dosyasını Buraya Sürükleyin"}
              </div>
              <div className="text-xs sm:text-sm text-slate-500 mt-1">
                veya <span className="text-sky-600 font-bold underline">masaüstünden seçmek için tıklayın</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-2">
                Desteklenen formatlar: .xlsx · .xlsm · .csv (maks. 20 MB) · Beklenen sütunlar: <b>Tarih</b>, <b>Plaka</b>, <b>Litre</b>, <b>KM/ÇS</b>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingLast && !analysis && (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-sky-500" size={32} /></div>
      )}

      {/* Sheet selector — shown when file has multiple sheets */}
      {analysis?.all_sheets?.length > 1 && (
        <div className="site-card p-4 sm:p-5 rounded-lg" data-testid="sheet-selector">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">Excel Sheet Seçimi</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Sadece seçili sheet'ler analiz edilir. İşlenen: <b>{analysis.processed_sheets?.length || 0}</b> / {analysis.all_sheets.length}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={selectAllSheets} className="text-xs font-bold text-sky-600 hover:underline" data-testid="select-all-sheets">Tümünü Seç</button>
              <span className="text-slate-300">·</span>
              <button type="button" onClick={clearSheets} className="text-xs font-bold text-slate-500 hover:underline" data-testid="clear-sheets">Temizle</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {analysis.all_sheets.map((sn) => {
              const isSel = selectedSheets?.has(sn);
              const wasProcessed = analysis.processed_sheets?.includes(sn);
              return (
                <button
                  key={sn}
                  type="button"
                  onClick={() => toggleSheet(sn)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                    isSel
                      ? "bg-sky-500 border-sky-500 text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:border-sky-400"
                  }`}
                  data-testid={`sheet-chip-${sn.replace(/\s/g, "_")}`}
                  title={wasProcessed ? "Son analize dahil edildi" : "Analize dahil edilmedi"}
                >
                  {isSel && <CheckCircle2 size={12} className="inline mr-1 -mt-0.5" />}
                  {sn}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] text-slate-500">
              {selectedSheets?.size || 0} sheet seçili.
              {!currentFile && analysis && <span className="text-amber-600 ml-2">Yeniden analiz için dosyayı tekrar yükleyin (sayfa yenilendikten sonra dosya yerel bellekten temizlenir).</span>}
            </div>
            <Button
              size="sm"
              className="bg-sky-500 hover:bg-sky-600 text-white font-bold h-9"
              onClick={reAnalyze}
              disabled={uploading || !currentFile || !selectedSheets?.size}
              data-testid="reanalyze-btn"
            >
              {uploading ? <Loader2 className="animate-spin" size={14} /> : "Seçili Sheet'lerle Yeniden Analiz Et"}
            </Button>
          </div>
        </div>
      )}

      {!loadingLast && !analysis?.vehicles?.length && !uploading && (
        <div className="site-card p-8 rounded-lg text-center" data-testid="fuel-empty-state">
          <Info size={32} className="text-slate-400 mx-auto mb-3" />
          <div className="font-bold text-slate-900">Henüz yakıt analizi yok</div>
          <div className="text-sm text-slate-500 mt-1">
            {isEditor ? "Yukarıdan bir Excel dosyası yükleyerek başlayın." : "Yönetici tarafından yakıt dosyası yüklenmesi gerekiyor."}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPICard label="Toplam Araç" value={s.total_vehicles} subtext={`${s.road_vehicles} karayolu · ${s.machines} iş makinesi`} icon={Truck} tone="sky" testId="kpi-total-vehicles" />
          <KPICard label="Toplam Yol" value={formatNumber(s.total_distance_km) + " km"} subtext={s.total_hours > 0 ? `+ ${formatNumber(s.total_hours)} sa` : "karayolu filo"} icon={ArrowUpDown} tone="slate" testId="kpi-total-km" />
          <KPICard
            label="Toplam Yakıt"
            value={formatNumber(s.total_litre) + " L"}
            subtext={s.total_amount ? `≈ ${formatNumber(s.total_amount)} ₺` : "TL bilgisi yok"}
            icon={Fuel} tone="amber" testId="kpi-total-litre"
          />
          <KPICard
            label="Ort. km/L (Karayolu)"
            value={s.road_avg_km_per_l > 0 ? formatNumber(s.road_avg_km_per_l) : "—"}
            subtext={s.worst_plate ? `En düşük: ${s.worst_plate} (${formatNumber(s.worst_km_per_l)} km/L)` : ""}
            icon={TrendingUp} tone={s.road_avg_km_per_l >= 3 ? "green" : "red"} testId="kpi-avg-kml"
          />
        </div>
      )}

      {/* Best & Worst callouts */}
      {s && (s.best_plate || s.worst_plate) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {s.best_plate && (
            <div className="site-card p-4 rounded-lg border-l-4 border-green-500" data-testid="best-vehicle">
              <div className="flex items-center gap-2 text-green-700"><TrendingUp size={18} /><span className="text-[10px] font-black uppercase tracking-widest">En Verimli Karayolu Aracı</span></div>
              <div className="font-mono-num font-black text-xl text-slate-900 mt-1">{s.best_plate}</div>
              <div className="text-sm text-slate-600">{formatNumber(s.best_km_per_l)} km/L · {formatNumber(s.best_l_per_km)} L/km</div>
            </div>
          )}
          {s.worst_plate && (
            <div className="site-card p-4 rounded-lg border-l-4 border-red-500" data-testid="worst-vehicle">
              <div className="flex items-center gap-2 text-red-700"><TrendingDown size={18} /><span className="text-[10px] font-black uppercase tracking-widest">En Düşük Verim (Yüksek Tüketim)</span></div>
              <div className="font-mono-num font-black text-xl text-slate-900 mt-1">{s.worst_plate}</div>
              <div className="text-sm text-slate-600">{formatNumber(s.worst_km_per_l)} km/L · {formatNumber(s.worst_l_per_km)} L/km · Muhtemelen bakım gerekli</div>
            </div>
          )}
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="site-card p-4 sm:p-6 rounded-lg" data-testid="fuel-chart">
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display mb-4">
            Verimlilik Grafiği <span className="text-xs font-bold text-slate-500 normal-case tracking-normal">(en düşük verimli 15 karayolu aracı — km/L)</span>
          </h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="plate" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0} height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v) => [`${formatNumber(v)} km/L`, "Verim"]}
                  contentStyle={{ borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }}
                />
                <Bar dataKey="km_per_litre" radius={[4, 4, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.status === "high" ? "#ef4444" : d.status === "low" ? "#22c55e" : "#0ea5e9"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Table */}
      {analysis?.vehicles?.length > 0 && (
        <div className="site-card p-4 sm:p-6 rounded-lg" data-testid="fuel-table-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display">Araç Bazlı Detay Analiz</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Plaka veya şoför ara..." value={search} onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 w-48 border-2" data-testid="fuel-search" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 w-40 border-2" data-testid="fuel-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Tipler</SelectItem>
                  <SelectItem value="Karayolu">Karayolu</SelectItem>
                  <SelectItem value="İş Makinesi">İş Makinesi</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-40 border-2" data-testid="fuel-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Durumlar</SelectItem>
                  <SelectItem value="high">Yüksek Tüketim</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Verimli</SelectItem>
                  <SelectItem value="unknown">Veri Yetersiz</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 text-xs uppercase tracking-widest text-slate-500">
                  <th className="text-left py-2.5 px-2 font-bold cursor-pointer hover:text-sky-600" onClick={() => toggleSort("plate")}>Plaka</th>
                  <th className="text-left py-2.5 px-2 font-bold">Tip</th>
                  <th className="text-right py-2.5 px-2 font-bold cursor-pointer hover:text-sky-600" onClick={() => toggleSort("distance")} title="Son km − İlk km">Yol / Saat</th>
                  <th className="text-right py-2.5 px-2 font-bold cursor-pointer hover:text-sky-600" onClick={() => toggleSort("total_litre")}>Toplam Litre</th>
                  <th className="text-right py-2.5 px-2 font-bold cursor-pointer hover:text-sky-600" onClick={() => toggleSort("unit_per_litre")} title="1 litre ile gidilen km / saat — yüksek = verimli">km/L · sa/L</th>
                  <th className="text-right py-2.5 px-2 font-bold cursor-pointer hover:text-sky-600" onClick={() => toggleSort("l_per_unit")} title="1 km / saat için tüketilen litre — düşük = verimli">L/km · L/sa</th>
                  <th className="text-right py-2.5 px-2 font-bold cursor-pointer hover:text-sky-600" onClick={() => toggleSort("fill_count")}>Doldurma</th>
                  <th className="text-left py-2.5 px-2 font-bold">Durum</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => {
                  const st = STATUS_STYLES[v.status] || STATUS_STYLES.normal;
                  return (
                    <tr
                      key={v.plate}
                      className={`border-b border-slate-100 ${v.status === "high" ? "bg-red-50/40" : ""}`}
                      data-testid={`fuel-row-${v.plate.replace(/\s/g, "_")}`}
                    >
                      <td className="py-2.5 px-2 font-mono-num font-bold text-slate-900">{v.plate}</td>
                      <td className="py-2.5 px-2">
                        <span className="inline-flex items-center gap-1 text-xs font-bold">
                          {v.vehicle_type === "Karayolu" ? <Truck size={12} className="text-sky-600" /> : <Cog size={12} className="text-amber-600" />}
                          {v.vehicle_type}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono-num text-slate-800">
                        {formatNumber(v.distance)} <span className="text-slate-400">{v.unit}</span>
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono-num text-amber-700 font-bold">{formatNumber(v.total_litre)} L</td>
                      <td className="py-2.5 px-2 text-right font-mono-num font-black" style={{ color: v.status === "high" ? "#b91c1c" : v.status === "low" ? "#15803d" : "#0f172a" }}>
                        {v.unit_per_litre != null ? formatNumber(v.unit_per_litre) : "—"}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono-num text-slate-600">
                        {v.l_per_unit != null ? formatNumber(v.l_per_unit) : "—"}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono-num text-slate-500">{v.fill_count}</td>
                      <td className="py-2.5 px-2">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${st.bg} ${st.border} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-sm text-slate-500">Arama kriterlerine uyan araç yok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-500 mt-3">
            {filtered.length} / {analysis.vehicles.length} araç gösteriliyor · <b>Hesaplama:</b> Yol = Son KM − İlk KM. Toplam Litre = Tüm doldurmalar toplamı <b>eksi ilk doldurma</b> (araç depo dolu teslim edildiği kabulüyle). km/L = Yol / Toplam Litre.
          </div>
        </div>
      )}

      {/* Warnings */}
      {analysis?.warnings?.length > 0 && (
        <div className="site-card p-4 sm:p-5 rounded-lg border-l-4 border-amber-400" data-testid="fuel-warnings">
          <div className="flex items-center gap-2 text-amber-800 mb-2">
            <AlertTriangle size={16} />
            <span className="font-black uppercase tracking-widest text-xs">İşleme Uyarıları ({analysis.warnings.length})</span>
          </div>
          <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
            {analysis.warnings.map((w, i) => (<li key={i}>{w}</li>))}
          </ul>
        </div>
      )}
    </div>
  );
}
